import { useCallback, useEffect, useRef, useState } from 'react'
import {
  listTopics,
  createTopic,
  updateTopic,
  deleteTopic,
  duplicateTopic,
  bulkDeleteTopics,
} from '../services/bookmarks'

const byName = (a, b) => (a.name || '').localeCompare(b.name || '')

// Loads the current user's topics and lets callers create one inline (used by
// the bookmark modals' topic picker and the rail's "new topic" action).
export function useTopics() {
  const [topics, setTopics] = useState([])
  const [status, setStatus] = useState('loading') // loading|ready|error
  const requestRef = useRef(0)

  const load = useCallback(() => {
    const request = ++requestRef.current
    setStatus((current) => (current === 'ready' ? current : 'loading'))
    listTopics()
      .then((list) => {
        if (request !== requestRef.current) return
        setTopics([...list].sort(byName))
        setStatus('ready')
      })
      .catch(() => {
        if (request === requestRef.current) setStatus('error')
      })
    return () => {
      if (requestRef.current === request) requestRef.current += 1
    }
  }, [])

  useEffect(() => load(), [load])

  // Create a topic, insert it into the local list (kept alphabetical), and return
  // it so the caller can auto-select it.
  const createOne = useCallback(async (name, parentId) => {
    const topic = await createTopic(parentId ? { name, parentId } : { name })
    setTopics((prev) => [...prev, topic].sort(byName))
    return topic
  }, [])

  // Rename a topic in place (keeps the list alphabetical). The rename response
  // has no `count`, so preserve the one we already hold.
  const renameOne = useCallback(async (id, name) => {
    const topic = await updateTopic(id, { name })
    setTopics((prev) =>
      prev.map((t) => (t._id === id ? { ...topic, count: t.count } : t)).sort(byName)
    )
    return topic
  }, [])

  // Delete a topic; bookmarks are kept (the server detaches the topic from them).
  const removeOne = useCallback(async (id) => {
    await deleteTopic(id)
    setTopics((prev) => prev.filter((t) => t._id !== id))
  }, [])

  // Delete several topics at once; bookmarks are kept.
  const removeMany = useCallback(async (ids) => {
    await bulkDeleteTopics(ids)
    const drop = new Set(ids.map(String))
    setTopics((prev) => prev.filter((t) => !drop.has(String(t._id))))
  }, [])

  // Duplicate a topic (empty copy, name only); insert it into the list.
  const duplicateOne = useCallback(async (id) => {
    const topic = await duplicateTopic(id)
    setTopics((prev) => [...prev, topic].sort(byName))
    return topic
  }, [])

  return {
    topics,
    status,
    createTopic: createOne,
    renameTopic: renameOne,
    removeTopic: removeOne,
    removeTopics: removeMany,
    duplicateTopic: duplicateOne,
    reload: load,
  }
}
